import { PublicAxiosInstance } from '@/api/axios'
import { endPoint } from '@/constant/end-point'
import { streamAiGeneration } from '@/utils/sse-stream'
import { useQueryClient, type QueryKey } from '@tanstack/react-query'
import type { AxiosInstance } from 'axios'
import { isAxiosError } from 'axios'
import { useEffect, useRef, useState } from 'react'

const HTTP_TOO_MANY_REQUESTS = 429
const STREAM_THROTTLE_MS = 60

const getSummaryErrorMessage = (err: unknown): string => {
  if (isAxiosError(err) && err.response?.status === HTTP_TOO_MANY_REQUESTS)
    return 'Too many summary requests. Please try again later.'
  return err instanceof Error ? err.message : 'Failed to summarise this policy.'
}

/**
 * Stream an AI policy summary. Completed summaries are kept in the query cache
 * so reopening a policy does not spend another (rate limited) request.
 */
const useStreamedPolicySummary = ({
  cacheKey,
  endpoint,
  axiosInstance,
}: {
  cacheKey: QueryKey
  endpoint: string
  axiosInstance?: AxiosInstance
}) => {
  const queryClient = useQueryClient()

  const [content, setContent] = useState(() => {
    const cached = queryClient.getQueryData<unknown>(cacheKey)
    return typeof cached === 'string' ? cached : ''
  })
  const [isStreaming, setIsStreaming] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const abortRef = useRef<AbortController | null>(null)

  useEffect(() => () => abortRef.current?.abort(), [])

  const summarise = async () => {
    if (content || isStreaming) return

    const controller = new AbortController()
    abortRef.current = controller
    setIsStreaming(true)
    setError(null)

    try {
      const finalContent = await streamAiGeneration({
        endpoint,
        body: {},
        axiosInstance,
        signal: controller.signal,
        throttleMs: STREAM_THROTTLE_MS,
        onContent: setContent,
      })
      queryClient.setQueryData(cacheKey, finalContent)
    } catch (err) {
      if (controller.signal.aborted) return
      setContent('')
      setError(getSummaryErrorMessage(err))
    } finally {
      if (!controller.signal.aborted) setIsStreaming(false)
    }
  }

  return { content, isStreaming, error, summarise }
}

/** AI summary of a policy shared through a single-policy or multi-policy acknowledgement link. */
export const usePolicyAiSummary = ({ token, policyHistoryId }: { token: string; policyHistoryId: string }) =>
  useStreamedPolicySummary({
    cacheKey: ['policy-shared-summary-text', token, policyHistoryId],
    endpoint: `/policy/secure/${token}/${policyHistoryId}/summary`,
    axiosInstance: PublicAxiosInstance,
  })

/**
 * AI summary of a policy's current content for a signed-in organisation user.
 * `updatedAt` is part of the cache key so an edited policy is summarised again.
 */
export const useOrganisationPolicyAiSummary = ({ policyId, updatedAt }: { policyId: string; updatedAt?: string }) =>
  useStreamedPolicySummary({
    cacheKey: ['policy-ai-summary-text', policyId, updatedAt],
    endpoint: `${endPoint.organisation.policy.summaryStream}/${policyId}/stream`,
  })
